import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, Inbox, Instagram, LogOut, Pencil, Plus, Save, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "../../seo";
import { api } from "../../api";

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("articles");
    const [articles, setArticles] = useState([]);
    const [enquiries, setEnquiries] = useState([]);
    const [reelsText, setReelsText] = useState("");
    const [savingReels, setSavingReels] = useState(false);
    const [igStatus, setIgStatus] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [reviewsList, setReviewsList] = useState([]);
    const [savingReviews, setSavingReviews] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            await api.get("/auth/me");
            const [a, e, r] = await Promise.all([api.get("/admin/articles"), api.get("/admin/enquiries"), api.get("/settings/reels")]);
            setArticles(a.data.articles);
            setEnquiries(e.data.enquiries);
            setReelsText(r.data.reels.join("\n"));
            api.get("/admin/instagram/status").then((s) => setIgStatus(s.data)).catch(() => {});
            api.get("/settings/reviews").then((s) => setReviewsList(s.data.reviews)).catch(() => {});
        } catch {
            navigate("/admin");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => { load(); }, [load]);

    const saveReels = async () => {
        setSavingReels(true);
        try {
            const reels = reelsText.split("\n").map((s) => s.trim()).filter(Boolean);
            const r = await api.put("/admin/settings/reels", { reels });
            setReelsText(r.data.reels.join("\n"));
            toast.success("Instagram reels updated — live on the homepage now.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not save reels.");
        } finally {
            setSavingReels(false);
        }
    };

    const syncNow = async () => {        setSyncing(true);
        try {
            const r = await api.post("/admin/instagram/sync-now");
            toast.success(`Sync complete — ${r.data.reel_count} reels found.`);
            const s = await api.get("/admin/instagram/status");
            setIgStatus(s.data);
            const reels = await api.get("/settings/reels");
            setReelsText(reels.data.reels.join("\n"));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Sync failed.");
        } finally {
            setSyncing(false);
        }
    };

    const addReview = () => setReviewsList((prev) => [...prev, { name: "", text: "", rating: 5, date: "" }]);
    const removeReview = (i) => setReviewsList((prev) => prev.filter((_, x) => x !== i));
    const updateReview = (i, k, v) => setReviewsList((prev) => prev.map((r, x) => x === i ? { ...r, [k]: v } : r));
    const saveReviews = async () => {
        const cleaned = reviewsList.filter((r) => r.name.trim() && r.text.trim());
        setSavingReviews(true);
        try {
            const r = await api.put("/admin/settings/reviews", { reviews: cleaned });
            setReviewsList(r.data.reviews);
            toast.success("Reviews updated — live on the homepage now.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not save reviews.");
        } finally {
            setSavingReviews(false);
        }
    };

    const logout = async () => {
        await api.post("/auth/logout").catch(() => {});
        navigate("/admin");
    };

    const deleteArticle = async (slug) => {
        if (!window.confirm(`Delete article "${slug}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/articles/${slug}`);
            setArticles((prev) => prev.filter((a) => a.slug !== slug));
            toast.success("Article deleted.");
        } catch {
            toast.error("Could not delete article.");
        }
    };

    if (loading) return <div className="flex min-h-screen items-center justify-center text-steel">Loading…</div>;

    return (
        <div className="min-h-screen bg-mist">
            <Seo title="Admin Dashboard | Dr. Krishnakanth AVB" description="Admin area." />
            <header className="border-b border-border bg-white" data-testid="admin-header">
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
                    <p className="font-display font-semibold text-navy">Content Manager</p>
                    <button onClick={logout} data-testid="admin-logout-btn"
                        className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs font-semibold text-steel hover:text-navy">
                        <LogOut className="h-3.5 w-3.5" /> Sign out
                    </button>
                </div>
            </header>
            <div className="mx-auto max-w-6xl px-5 py-10">
                <div className="flex gap-2" data-testid="admin-tabs">
                    <button onClick={() => setTab("articles")} data-testid="tab-articles"
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "articles" ? "bg-navy text-white" : "bg-white border border-border text-steel"}`}>
                        <FileText className="h-4 w-4" /> Articles ({articles.length})
                    </button>
                    <button onClick={() => setTab("enquiries")} data-testid="tab-enquiries"
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "enquiries" ? "bg-navy text-white" : "bg-white border border-border text-steel"}`}>
                        <Inbox className="h-4 w-4" /> Enquiries ({enquiries.length})
                    </button>
                    <button onClick={() => setTab("reels")} data-testid="tab-reels"
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "reels" ? "bg-navy text-white" : "bg-white border border-border text-steel"}`}>
                        <Instagram className="h-4 w-4" /> Instagram Reels
                    </button>
                    <button onClick={() => setTab("reviews")} data-testid="tab-reviews"
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "reviews" ? "bg-navy text-white" : "bg-white border border-border text-steel"}`}>
                        <Star className="h-4 w-4" /> Reviews ({reviewsList.length})
                    </button>
                </div>

                {tab === "articles" && (
                    <div className="mt-8" data-testid="admin-articles-panel">
                        <Link to="/admin/articles/new" data-testid="new-article-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white hover:bg-accent">
                            <Plus className="h-4 w-4" /> New article
                        </Link>
                        <div className="mt-6 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-white">
                            {articles.map((a) => (
                                <div key={a.slug} className="flex flex-wrap items-center justify-between gap-4 px-6 py-4" data-testid={`admin-article-row-${a.slug}`}>
                                    <div>
                                        <p className="font-medium text-navy">{a.title}</p>
                                        <p className="mt-1 text-xs text-steel">{a.category} · {a.date} · /patient-education/{a.slug}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Link to={`/admin/articles/${a.slug}`} data-testid={`edit-article-${a.slug}`}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-semibold text-navy hover:border-accent hover:text-accent">
                                            <Pencil className="h-3.5 w-3.5" /> Edit
                                        </Link>
                                        <button onClick={() => deleteArticle(a.slug)} data-testid={`delete-article-${a.slug}`}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-semibold text-red-600 hover:border-red-300">
                                            <Trash2 className="h-3.5 w-3.5" /> Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {articles.length === 0 && <p className="px-6 py-10 text-center text-sm text-steel">No articles yet.</p>}
                        </div>
                    </div>
                )}

                {tab === "reviews" && (
                    <div className="mt-8 rounded-2xl border border-border bg-white p-7" data-testid="admin-reviews-panel">
                        <h2 className="font-display text-xl font-medium text-navy">Homepage Google reviews</h2>
                        <p className="mt-2 text-sm leading-relaxed text-steel">
                            When a patient posts a Google review you would like to feature, copy it here. Reviews appear in the
                            scrolling row on the homepage in this order. Only copy real reviews from your Google Business Profile.
                        </p>
                        <div className="mt-6 space-y-5">
                            {reviewsList.map((r, i) => (
                                <div key={i} className="rounded-xl border border-border bg-mist p-5" data-testid={`review-edit-${i}`}>
                                    <div className="grid gap-4 sm:grid-cols-[1fr_120px_140px]">
                                        <input value={r.name} onChange={(e) => updateReview(i, "name", e.target.value)} placeholder="Patient name (as on Google)"
                                            data-testid={`review-name-${i}`}
                                            className="rounded-lg border border-input bg-white px-3 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-accent" />
                                        <select value={r.rating} onChange={(e) => updateReview(i, "rating", parseInt(e.target.value, 10))}
                                            data-testid={`review-rating-${i}`}
                                            className="rounded-lg border border-input bg-white px-3 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-accent">
                                            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
                                        </select>
                                        <input value={r.date} onChange={(e) => updateReview(i, "date", e.target.value)} placeholder="e.g. Sep 2026"
                                            data-testid={`review-date-${i}`}
                                            className="rounded-lg border border-input bg-white px-3 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-accent" />
                                    </div>
                                    <textarea rows={2} value={r.text} onChange={(e) => updateReview(i, "text", e.target.value)} placeholder="Review text, copied from Google"
                                        data-testid={`review-text-${i}`}
                                        className="mt-3 w-full rounded-lg border border-input bg-white px-3 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-accent" />
                                    <button onClick={() => removeReview(i)} data-testid={`review-delete-${i}`}
                                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:underline">
                                        <Trash2 className="h-3.5 w-3.5" /> Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="mt-5 flex gap-3">
                            <button onClick={addReview} data-testid="add-review-btn"
                                className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-5 py-2.5 text-sm font-semibold text-navy hover:border-accent hover:text-accent">
                                <Plus className="h-4 w-4" /> Add review
                            </button>
                            <button onClick={saveReviews} disabled={savingReviews} data-testid="save-reviews-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-2.5 text-sm font-semibold text-white hover:bg-accent disabled:opacity-60">
                                <Save className="h-4 w-4" /> {savingReviews ? "Saving…" : "Save reviews"}
                            </button>
                        </div>
                    </div>
                )}

                {tab === "reels" && (
                    <div className="mt-8 rounded-2xl border border-border bg-white p-7" data-testid="admin-reels-panel">
                        {igStatus && (
                            <div className="mb-6 rounded-xl border border-border bg-mist p-5" data-testid="ig-autosync-status">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <p className="text-sm font-semibold text-navy">
                                        Automatic weekly sync:{" "}
                                        {igStatus.connected
                                            ? <span className="text-emerald-700">Active — connected as @{igStatus.username}</span>
                                            : <span className="text-amber-600">Not connected yet</span>}
                                    </p>
                                    {igStatus.connected && (
                                        <button onClick={syncNow} disabled={syncing} data-testid="ig-sync-now-btn"
                                            className="rounded-full border border-navy/20 px-4 py-2 text-xs font-semibold text-navy hover:border-accent hover:text-accent disabled:opacity-60">
                                            {syncing ? "Syncing…" : "Sync now"}
                                        </button>
                                    )}
                                </div>
                                {igStatus.connected && igStatus.last_sync && (
                                    <p className="mt-2 text-xs text-steel">
                                        Last sync: {new Date(igStatus.last_sync).toLocaleString()} · {igStatus.reel_count} reels found · newest reels appear on the homepage automatically every Monday.
                                    </p>
                                )}
                                {!igStatus.connected && igStatus.authorize_url && (
                                    <a href={igStatus.authorize_url} data-testid="ig-connect-btn"
                                        className="mt-3 inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-semibold text-white hover:bg-accent">
                                        <Instagram className="h-3.5 w-3.5" /> Connect Instagram account
                                    </a>
                                )}
                            </div>
                        )}
                        <h2 className="font-display text-xl font-medium text-navy">Homepage Instagram reels{igStatus?.connected ? " (manual fallback)" : ""}</h2>
                        <p className="mt-2 text-sm leading-relaxed text-steel">
                            Paste one Instagram reel link per line (the full link or just the code — both work).
                            They appear in the scrolling video row on the homepage in this order. Remove a line to remove that reel.
                        </p>
                        <textarea rows={8} value={reelsText} onChange={(e) => setReelsText(e.target.value)}
                            data-testid="reels-textarea"
                            className="mt-5 w-full rounded-lg border border-input bg-white px-4 py-3 font-mono text-sm text-navy focus:outline-none focus:ring-2 focus:ring-accent"
                            placeholder={"https://www.instagram.com/reel/DcQ6SW2ierz/\nhttps://www.instagram.com/reel/DbWErzoi9_e/"} />
                        <button onClick={saveReels} disabled={savingReels} data-testid="save-reels-btn"
                            className="mt-5 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-accent disabled:opacity-60">
                            <Save className="h-4 w-4" /> {savingReels ? "Saving…" : "Save reels"}
                        </button>
                    </div>
                )}

                {tab === "enquiries" && (
                    <div className="mt-8 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-white" data-testid="admin-enquiries-panel">
                        {enquiries.map((e) => (
                            <div key={e.enquiry_id} className="px-6 py-5" data-testid={`enquiry-${e.enquiry_id}`}>
                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                    <p className="font-medium text-navy">{e.name} <span className="ml-2 text-sm font-normal text-steel">{e.phone}</span></p>
                                    <p className="text-xs text-steel">{new Date(e.created_at).toLocaleString()}</p>
                                </div>
                                <p className="mt-1 text-sm text-steel">
                                    {e.preferred_date || "Any date"} · {e.preferred_time || "Any time"}
                                    {e.email ? ` · ${e.email}` : ""}
                                </p>
                                {e.reason && <p className="mt-2 text-sm leading-relaxed text-slate-600">"{e.reason}"</p>}
                            </div>
                        ))}
                        {enquiries.length === 0 && <p className="px-6 py-10 text-center text-sm text-steel">No enquiries yet.</p>}
                    </div>
                )}
            </div>
        </div>
    );
}
